import { useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import {
  ProductSortByParamParameter,
  SearchBrandScheme,
  SortDirectionParamParameter,
} from '@/apis/rest/generated/types'
import ProductFilterTopBarForV4 from '@/components/common/etc/ProductFilterTopBarForV4'
import useSearchedKeywords from '@/components/domains/search/hooks/useSearchedKeywords'
import { SearchResultProductList } from '@/components/domains/search/result/SearchResultProductList'
import { DEFAULT } from '@/constants/default.const'
import { PAGE_LIMIT } from '@/constants/pages.const'
import { ProductLocationTrackerValue } from '@/constants/product-location.const'
import { PRODUCTS_SELECT_FILTER_ORDER_BY_V4 } from '@/constants/products-select-filter-orderby.const'
import { QueryStringValueEnum } from '@/constants/query-string-value.enum'
import { TrackerOrderByEnum } from '@/constants/tracker-sort-by-value.enum'
import { useSearchEventTracker } from '@/containers/event-tracker/search.event-tracker'
import { useCustomRouter } from '@/containers/hooks'
import { useGetQueryParamsSearchResult } from '@/containers/hooks/search/use-get-query-params-search-result'
import { useQuerySearchBrandProducts } from '@/containers/hooks/search/use-query-search-brand-products'
import { useSearchRecentlyBought } from '@/containers/search/search-recently-boughout.hook'
import { SearchResultContainer } from '@/containers/search/search-result.context'
import { AdPetType } from '@/graphql/generated/schemas'
import { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'
import { convertToProductCard } from '@/utils/product/product-card.util'
import { UtilUrlController } from '@/utils/utilUrlController'

type HandleClickProductCardTracker = ({
  item,
  index,
  properties,
}: {
  item: ProductTypeDefinedByFrontend
  index: number
  properties?: Record<string, unknown>
}) => void

export const SearchResultBody = () => {
  const { isShowSearchedKeywords } = useSearchedKeywords()
  const {
    trackViewSearchResultEvent,
    trackClickSearchListProductEvent,
    trackViewSearchResultFrequentProduct,
    trackViewSearchResultBrandProduct,
  } = useSearchEventTracker()

  const { asPath } = useCustomRouter()
  const { query, queryParams } = useGetQueryParamsSearchResult()

  const {
    petTypeQueryParam,
    searchKeywordQueryParam,
    searchLocation,
    sortBy,
    except,
    filterCategory,
    filterBrand,
    filterPrice,
    petTypePathParam,
    searchKeywordPathParam,
  } = query

  const {
    totalCount,
    soldOutCount,
    getCategoryNameById,
    getBrandNameById,
    isLoading: isSearchResultLoading,
  } = SearchResultContainer.useContainer()

  const { data: searchBrandProducts, isLoading: isSearchBrandProductsLoading } = useQuerySearchBrandProducts({
    ...queryParams,
    size: PAGE_LIMIT.SEARCH_BRAND,
    page: PAGE_LIMIT.DEFAULT_PAGE,
    sortBy: ProductSortByParamParameter.SALES_SCORE,
    sortDirection: SortDirectionParamParameter.DESC,
  })

  const searchKeyword = String(searchKeywordQueryParam || searchKeywordPathParam || '')
  const petType = UtilUrlController.urlParamToPetType(petTypeQueryParam || petTypePathParam)
  const isPetTypeSearchCat = petType === AdPetType.All || petType === AdPetType.Cat
  const isPetTypeSearchDog = petType === AdPetType.All || petType === AdPetType.Dog

  const { data: recentlyBoughtProducts, isRecentlyBoughtProductsLoading } = useSearchRecentlyBought({
    page: DEFAULT.defaultPage,
    size: DEFAULT.defaultSize,
    keyword: searchKeyword,
  })

  const handleClickProductCardTracker: HandleClickProductCardTracker = ({ item, index, properties = {} }) => {
    const args = {
      isPetTypeSearchCat,
      isPetTypeSearchDog,
      searchPetType: petType,
      searchKeyword,
      searchListIndex: index as number,
      searchResultCount: totalCount,
      ...(searchLocation ? { searchLocation } : {}),
      productId: item.id,
      productName: item.name,
      productPrice: item.price,
      productDiscountRate: item.discountRate || 0,
      productPurchasePrice: item.salePrice,
      productReviewAvgScore: item.reviewScore,
      productReviewTotalCount: item.reviewCount,
      is_shownprice_coupondiscounted: item.hasApplicableProductCoupon,
      productIsInStock: !item.isRealSoldOut,
      cum_sales_skuquantity: item.salesQuantity,
      max_mileage: item.expectedMileage,
      brandId: item.brand?.id,
      brandName: item.brand.name,
      supplyType: item.supplyType,
      location: 'search',
      addLocation: 'search_result',
      /**
       * JustViewedProductList에서 요구하는 특정 프로퍼티 때문에 properties를 넣어줍니다.
       */
      ...properties,
    }

    trackClickSearchListProductEvent(args)
  }

  /**
   * 검색결과 event-event-tracker 함수
   * @param directResultStatus {boolean} 검색 결과 최초진입 여부
   * 검색결과 최초 진입 후 필터, 정렬 등 한번이라도 조작을 하면 false로 바뀜
   */
  const trackingSearchResult = (directResultStatus: boolean) => {
    const orderByMapping: { [key: string]: TrackerOrderByEnum } = {
      [PRODUCTS_SELECT_FILTER_ORDER_BY_V4.RECOMMEND.value]: TrackerOrderByEnum.Recommend, // RECCOMENDED
      [PRODUCTS_SELECT_FILTER_ORDER_BY_V4.BEST_SELLING.value]: TrackerOrderByEnum.Popular, // BEST_SELLING
      [PRODUCTS_SELECT_FILTER_ORDER_BY_V4.NEWEST.value]: TrackerOrderByEnum.Recent, // NEWEST
      [PRODUCTS_SELECT_FILTER_ORDER_BY_V4.PRICE_ASC.value]: TrackerOrderByEnum.LowPrice,
      [PRODUCTS_SELECT_FILTER_ORDER_BY_V4.PRICE_DESC.value]: TrackerOrderByEnum.HighPrice,
      [PRODUCTS_SELECT_FILTER_ORDER_BY_V4.DISCOUNT_RATE.value]: TrackerOrderByEnum.Discount, // DISCOUNT_RATE
      [PRODUCTS_SELECT_FILTER_ORDER_BY_V4.REVIEW_COUNT.value]: TrackerOrderByEnum.Review, // REVIEW_COUNT
      [PRODUCTS_SELECT_FILTER_ORDER_BY_V4.REVIEW_SCORE.value]: TrackerOrderByEnum.ReviewScore, // REVIEW_SCORE
    }

    const eventProperties = {
      searchKeyword,
      petTypeSearch: petType,
      searchResultCount: totalCount,
      searchOutofstockCount: soldOutCount,
      excludeOutofstock: except === QueryStringValueEnum.SoldOut,
      searchLocation,
      isDirectResult: directResultStatus,
      sortBy: orderByMapping[sortBy as string] || TrackerOrderByEnum.Recommend,
      filterCategory: getCategoryNameById(filterCategory as string),
      filterBrand: getBrandNameById(filterBrand as string),
      filterPriceMin: filterPrice ? Number((filterPrice as string).split('~')[0]) : null,
      filterPriceMax: filterPrice ? Number((filterPrice as string).split('~')[1]) : null,
    }

    trackViewSearchResultEvent(eventProperties)
  }

  useEffect(() => {
    /**
     * 최근 데이터가 없더라도 loading 중이라면 tracking 을 하지 않음
     */
    if (isSearchResultLoading || isRecentlyBoughtProductsLoading) {
      return
    }

    /**
     * search_result, search_frequent_product tracker 순서대로 전송해달라고 요청받음 (2024-08-06 by Elmo)
     */

    if (!isRecentlyBoughtProductsLoading && recentlyBoughtProducts?.length > 0) {
      trackViewSearchResultFrequentProduct({
        searchKeyword,
        petType,
        boughtCount: recentlyBoughtProducts[0].boughtCount,
        product: convertToProductCard(recentlyBoughtProducts[0].product),
        searchLocation: ProductLocationTrackerValue.SearchResultFrequent,
      })
    }
    // query Params 를 포함 한 URL 이 바뀔떄 실행
  }, [asPath, isSearchResultLoading, isRecentlyBoughtProductsLoading])

  useEffect(() => {
    if (isSearchResultLoading) {
      return
    }
    trackingSearchResult(searchLocation === 'submit')
    /**
     * page view 이벤트 실행 후 브랜드 영역 트래커 실행
     * 서치 결과 API보다 브랜드 영역 API가 일찍 호출 됨
     * search_result, search_frequent_product tracker 순서대로 전송해달라고 요청받음 (2024-08-06 by Elmo)
     * 2024-10-30 브랜드 영역 상품 트래커도 동일하게 처리
     */

    if (isSearchBrandProductsLoading) {
      return
    }
    if (!isSearchBrandProductsLoading && searchBrandProducts?.products && searchBrandProducts?.products?.length > 0) {
      trackViewSearchResultBrandProduct({
        searchKeyword,
        petType,
        searchLocation: ProductLocationTrackerValue.SearchResultFrequent,
        brand: searchBrandProducts?.brands?.[0] as SearchBrandScheme,
      })
    }
    // query Params 를 포함 한 URL 이 바뀔떄 실행
  }, [asPath, isSearchResultLoading, isSearchBrandProductsLoading])

  // query 검색어 누락 시 리턴
  if (!searchKeyword) {
    return null
  }

  return (
    <StyledSearchResultBodyContainer hasKeywords={isShowSearchedKeywords}>
      <ProductFilterTopBarForV4 />
      <SearchResultProductList
        searchKeyword={searchKeyword as string}
        searchLocation={searchLocation as string}
        searchBrandProducts={searchBrandProducts}
        petType={petType}
        recentlyBoughtProducts={recentlyBoughtProducts}
        onClickProductCardTracker={({ item, index, properties }) =>
          handleClickProductCardTracker({ item, index, properties })
        }
      />
    </StyledSearchResultBodyContainer>
  )
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const StyledSearchResultBodyContainer = styled.div<{
  hasKeywords: boolean
}>`
  display: ${({ hasKeywords }) => (hasKeywords ? 'none' : 'block')};
  animation: ${fadeIn} 0.5s ease-in-out;
`
